// Optelanimatie voor de cijfers in het statistiekenblok (`_blocks/stats.twig`).
//
// De eindwaarde staat in de HTML en komt van de server: zonder JS en voor
// zoekmachines staat het juiste getal er dus gewoon. Dit script zet het cijfer
// op 0 en telt terug op naar diezelfde tekst, en zet aan het eind letterlijk de
// oorspronkelijke tekst terug, zodat opmaak met een punt of een komma
// ("1.250", "4,5") niet kan wegvallen op een afrondingsfout.
//
// WAAROM GEEN EIGEN INTERSECTIONOBSERVER
// `animations.js` observeert al elke `.lava` en `.lavaCont` en zet er de class
// `animate` op zodra het blok in beeld komt, met een eigen drempel (15% van de
// onderkant van het scherm) en een stagger van 300ms per rij. Een tweede
// observer zou op een ander moment afgaan dan de fade van diezelfde rij: het
// cijfer begint dan te tellen terwijl de rij nog onzichtbaar is, of omgekeerd.
// Daarom kijken we met een MutationObserver naar die ene class. Zo blijft er
// precies één observer die beslist wanneer iets in beeld is, en telt elk cijfer
// mee op het ritme van zijn eigen rij.

document.addEventListener("DOMContentLoaded", () => {
  const SELECTOR = "[data-count-up]";
  const ANIMATE_CLASS = "animate";
  const DRAGER_SELECTOR = ".lava, .lavaCont";

  // 1200ms: lang genoeg om het optellen te zien (op 60fps zijn dat ~72 stappen,
  // dus ook een klein getal als 45 loopt niet in sprongen), kort genoeg om al
  // klaar te zijn voor iemand doorscrollt. De fade van `.lava` duurt 600ms, dus
  // het cijfer staat stil kort nadat de rij helemaal zichtbaar is.
  const DUUR = 1200;

  const elementen = document.querySelectorAll(SELECTOR);
  if (!elementen.length) return;

  // Wie liever geen beweging ziet, krijgt meteen de eindwaarde: die staat al in
  // de HTML, dus hier is niets te doen. `animations.js` doet hetzelfde voor de
  // fades. Zonder deze tak zou het cijfer alsnog van 0 opklimmen, want de
  // trigger (`animate`) wordt daar in die situatie direct gezet.
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  // easeOutCubic: begint snel en remt af naar het eind. Past bij een teller,
  // want de laatste cijfers zijn de interessante: die zie je rustig op hun plek
  // vallen in plaats van er in één frame langs te schieten.
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  // Leest het server-gerenderde getal en onthoudt hoe het geschreven staat.
  // Onbekende opmaak geeft null terug: dan animeren we niet en blijft de tekst
  // staan zoals de klant hem heeft ingevuld.
  function leesGetal(tekst) {
    const schoon = tekst.trim();

    // Duizendgroepering: "1.250", "1,250", "1.250.000".
    const groep = schoon.match(/^\d{1,3}(([.,])\d{3})+$/);
    if (groep) {
      const scheider = groep[2];
      return {
        eind: Number(schoon.split(scheider).join("")),
        decimalen: 0,
        groepScheider: scheider,
        decScheider: null,
      };
    }

    // Decimaal: "4,5", "98.6". Maximaal twee cijfers achter de komma, anders
    // is het eerder een groepering of een jaartal-achtig iets.
    const decimaal = schoon.match(/^(\d+)([.,])(\d{1,2})$/);
    if (decimaal) {
      return {
        eind: Number(`${decimaal[1]}.${decimaal[3]}`),
        decimalen: decimaal[3].length,
        groepScheider: null,
        decScheider: decimaal[2],
      };
    }

    // Gewoon een heel getal: "205", "45", "98".
    if (/^\d+$/.test(schoon)) {
      return { eind: Number(schoon), decimalen: 0, groepScheider: null, decScheider: null };
    }

    return null;
  }

  // Schrijft een tussenwaarde in dezelfde opmaak als het ingevulde getal.
  function formatteer(waarde, spec) {
    const vast = waarde.toFixed(spec.decimalen);
    let [heel, deel] = vast.split(".");
    if (spec.groepScheider) {
      heel = heel.replace(/\B(?=(\d{3})+(?!\d))/g, spec.groepScheider);
    }
    return deel ? heel + spec.decScheider + deel : heel;
  }

  function telOp(el) {
    // Eén keer, ook als `animate` er later nog eens af en op gaat (dat gebeurt
    // bij het uit beeld scrollen voordat de observer het element loslaat).
    if (el.dataset.countUpGestart !== undefined) return;
    el.dataset.countUpGestart = "";

    const origineel = el.textContent;
    const spec = leesGetal(origineel);
    if (!spec) return;

    // De cijferkolom is `auto` breed en dus zo breed als de breedste inhoud.
    // Zonder maat krimpt die kolom naar de breedte van "0" zodra het tellen
    // begint en schuift de tekst ernaast ~100px heen en weer terug.
    //
    // Het is bewust `width` en niet `min-width`: Unbounded heeft géén
    // tabellarische cijfers, dus een tussenstand kan bréder zijn dan de
    // eindwaarde. Gemeten op 68px: "205" is 172,66px, maar "200" 180,67px en
    // "000" zelfs 187,05px. Met alleen een min-width groeide de kolom daardoor
    // halverwege het tellen 8px en sprong ze aan het eind terug. Met een vaste
    // breedte kan de kolom niet meer bewegen: een te brede tussenstand loopt
    // links buiten het kader (de inhoud staat rechts uitgelijnd), dus in de
    // lege ruimte vóór het cijfer en niet in de richting van de tekst.
    //
    // `font-variant-numeric: tabular-nums` zou dit ook oplossen, maar dat maakt
    // elk cijfer 53,05px breed en dus "205" 13,5px smaller dan in het ontwerp,
    // dat proportionele cijfers gebruikt (in het ontwerp meet "45" 106px, en
    // dat klopt met proportioneel, niet met tabellarisch).
    //
    // De maat gaat er weer af als we klaar zijn, zodat een lettergrootte die op
    // een breakpoint verandert niet aan een oude px-maat blijft hangen.
    el.style.width = `${el.getBoundingClientRect().width}px`;

    const begin = performance.now();

    function stap(nu) {
      const t = Math.min(1, (nu - begin) / DUUR);
      if (t < 1) {
        el.textContent = formatteer(spec.eind * easeOut(t), spec);
        requestAnimationFrame(stap);
      } else {
        // Letterlijk de servertekst terug, niet een herberekende waarde.
        el.textContent = origineel;
        el.style.width = "";
      }
    }

    requestAnimationFrame(stap);
  }

  elementen.forEach((el) => {
    const drager = el.closest(DRAGER_SELECTOR);

    // Staat het cijfer buiten het lava-systeem, dan is er niets om op te
    // wachten en telt het meteen op.
    if (!drager) {
      telOp(el);
      return;
    }

    if (drager.classList.contains(ANIMATE_CLASS)) {
      telOp(el);
      return;
    }

    const observer = new MutationObserver(() => {
      if (!drager.classList.contains(ANIMATE_CLASS)) return;
      observer.disconnect();
      telOp(el);
    });

    observer.observe(drager, { attributes: true, attributeFilter: ["class"] });
  });
});
